import { useState } from 'react';
import {
  Typography,
  TextField,
  Stack,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Button,
} from '@mui/material';

const PassLinkSettings = ({ deployment, onSave }) => {
  const [links, setLinks] = useState({
    backgroundImageLink: deployment?.config?.backgroundImageLink || '',
    logoLink: deployment?.config?.logoLink || '',
    impressumLink: deployment?.config?.impressumLink || '',
    privacyLink: deployment?.config?.privacyLink || '',
  });

  const [errors, setErrors] = useState({});
  const [defaultExpiryValue, setDefaultExpiryValue] = useState(
    deployment?.config?.defaultExpiryValue?.toString() || '1',
  );
  const [defaultExpiryUnit, setDefaultExpiryUnit] = useState(
    deployment?.config?.defaultExpiryUnit || 'Tage',
  );

  const handleLinkChange = (field) => (event) => {
    const value = event.target.value;
    setLinks((prev) => ({ ...prev, [field]: value }));

    // Validate URL if value is not empty
    if (value && !isValidUrl(value)) {
      setErrors((prev) => ({
        ...prev,
        [field]: 'Bitte geben Sie eine gültige URL ein',
      }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = () => {
    // Filter out empty links
    const configuredLinks = Object.entries(links).reduce(
      (acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      },
      {},
    );

    onSave({ ...configuredLinks, defaultExpiryValue, defaultExpiryUnit });
  };

  const linkFields = [
    { id: 'backgroundImageLink', label: 'Hintergrundbild URL' },
    { id: 'logoLink', label: 'Logo URL' },
    { id: 'impressumLink', label: 'Impressum URL' },
    { id: 'privacyLink', label: 'Datenschutz URL' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ my: 1, mb: 2, pr: '80px' }}>
        <Typography variant="h6">Personalisierung des Pass-Tools</Typography>
        <Divider />
      </Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Geben Sie optional die benötigten URLs ein, um Ihr Deployment zu
        personalisieren.
        <br />
        <span style={{ color: '#888', fontSize: '0.95em' }}>
          Leere Felder werden ignoriert.
        </span>
      </Typography>

      <Stack spacing={2}>
        {linkFields.map(({ id, label }) => (
          <TextField
            key={id}
            fullWidth
            label={label}
            value={links[id]}
            onChange={handleLinkChange(id)}
            error={!!errors[id]}
            helperText={errors[id] || ''}
            placeholder="https://"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
        ))}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
          <TextField
            label="Ablaufdauer"
            type="number"
            value={defaultExpiryValue}
            onChange={(e) => setDefaultExpiryValue(e.target.value.toString())}
            sx={{ width: 120 }}
            inputProps={{ min: 1 }}
            variant="outlined"
          />
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="expiry-unit-label">Einheit</InputLabel>
            <Select
              labelId="expiry-unit-label"
              value={defaultExpiryUnit}
              label="Einheit"
              onChange={(e) => setDefaultExpiryUnit(e.target.value)}
            >
              <MenuItem value="Tage">Tage</MenuItem>
              <MenuItem value="Wochen">Wochen</MenuItem>
              <MenuItem value="Stunden">Stunden</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Gültigkeit für generierte Links
          </Typography>
        </Box>
      </Stack>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        sx={{ mt: 'auto', ml: 'auto' }} // This pushes the button to the bottom right
      >
        Speichern
      </Button>
    </Box>
  );
};

export default PassLinkSettings;
